import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { companyI18n } from './i18n';
import { useCreateCompanyCurrency } from 'hooks/companyCurrencies/useCreateCompanyCurrency';
import { useUpdateCompanyCurrency } from 'hooks/companyCurrencies/useUpdateCompanyCurrency';
import { useRemoveCompanyCurrency } from 'hooks/companyCurrencies/useRemoveCompanyCurrency';
import { useFetchCompanyCurrencies } from 'hooks/companyCurrencies/useFetchCompanyCurrencies';
import { useSnackbar } from 'context/SnackbarContext';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';

const EMPTY_FORM = { name: '', symbol: '', locale: '', code: '' };

function mapRawCurrency(c) {
  return {
    companyCurrencyId: c.companyCurrencyId,
    displayName: c.customName || c.displayName || '',
    customName: c.customName,
    code: c.code || '',
    symbol: c.symbol || '',
    locale: c.locale || '',
    isEnabled: c.isEnabled,
  };
}

export default function CurrenciesModal({ Modal, Input, Button, show, onClose, onRefresh }) {
  const { language } = useLanguage();
  const companyLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: companyI18n });

  useEffect(() => {
    companyI18n.changeLanguage(companyLanguage);
  }, [companyLanguage]);

  const [view, setView] = useState('list');
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);

  const { showSnackbar } = useSnackbar();
  const { currencies: rawCurrencies, fetchCurrencies } = useFetchCompanyCurrencies();
  const { createCurrency, isLoading: loadingCreate } = useCreateCompanyCurrency();
  const { updateCurrency, isLoading: loadingUpdate } = useUpdateCompanyCurrency();
  const { removeCurrency, isLoading: loadingRemove } = useRemoveCompanyCurrency();

  const currencies = rawCurrencies.map(mapRawCurrency);
  const isEdit = !!selectedCurrency;
  const isSaving = loadingCreate || loadingUpdate;

  useEffect(() => {
    if (show) {
      setView('list');
      setSelectedCurrency(null);
      fetchCurrencies();
    }
  }, [show, fetchCurrencies]);

  const onChange = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const openCreate = () => {
    setSelectedCurrency(null);
    setForm(EMPTY_FORM);
    setView('form');
  };

  const openEdit = (currency) => {
    setSelectedCurrency(currency);
    setForm({
      name: currency.customName || currency.displayName || '',
      symbol: currency.symbol || '',
      locale: currency.locale || '',
      code: currency.code || '',
    });
    setView('form');
  };

  const openDelete = (currency) => {
    setCurrencyToDelete(currency);
    setShowConfirmDelete(true);
  };

  const handleSave = async () => {
    try {
      if (isEdit) {
        await updateCurrency(selectedCurrency.companyCurrencyId, {
          customName: form.name,
          isEnabled: true,
        });
        showSnackbar(t('company_currencies_updated'), 'success');
      } else {
        await createCurrency({
          name: form.name,
          symbol: form.symbol,
          locale: form.locale,
          code: form.code,
        });
        showSnackbar(t('company_currencies_created'), 'success');
      }
      await fetchCurrencies();
      await onRefresh?.();
      setView('list');
    } catch (err) {
      showSnackbar(
        err?.customMessage || (isEdit ? t('company_currencies_updateError') : t('company_currencies_createError')),
        'error'
      );
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await removeCurrency(currencyToDelete.companyCurrencyId);
      await fetchCurrencies();
      await onRefresh?.();
      showSnackbar(t('company_currencies_disabled'), 'success');
    } catch (err) {
      showSnackbar(err?.customMessage || t('company_currencies_disableError'), 'error');
    } finally {
      setShowConfirmDelete(false);
      setCurrencyToDelete(null);
    }
  };

  const handleReactivate = async (currency) => {
    try {
      await updateCurrency(currency.companyCurrencyId, { isEnabled: true });
      await fetchCurrencies();
      await onRefresh?.();
      showSnackbar(t('company_currencies_reactivated'), 'success');
    } catch (err) {
      showSnackbar(err?.customMessage || t('company_currencies_reactivateError'), 'error');
    }
  };

  const backToList = () => {
    setView('list');
    setSelectedCurrency(null);
  };

  const isFormValid = form.name.trim().length > 0;

  if (!Modal) return null;

  return (
    <>
      <Modal
        title={
          view === 'list'
            ? t('company_currencies_title')
            : isEdit
            ? t('company_currencies_editTitle')
            : t('company_currencies_addTitle')
        }
        show={show}
        onClose={onClose}
        primaryBtnTxt={view === 'list' ? t('company_currencies_close') : isSaving ? t('company_currencies_saving') : t('company_currencies_save')}
        primaryBtnAction={view === 'list' ? onClose : handleSave}
        disabledPrimaryBtn={view === 'form' && (!isFormValid || isSaving)}
        secondaryBtnTxt={view === 'form' ? t('company_currencies_cancel') : null}
        secondaryBtnAction={view === 'form' ? backToList : null}
        ctaAligment='right'
        width='720px'
      >
        {view === 'list' && (
          <div>
            <div className='d-flex justify-content-end mb-3'>
              {Button && (
                <Button
                  variant='primary'
                  iconStart='plusLg'
                  label={t('company_currencies_addBtn')}
                  size='sm'
                  onClick={openCreate}
                />
              )}
            </div>

            {currencies.length === 0 ? (
              <p className='caption font-light'>{t('company_currencies_empty')}</p>
            ) : (
              <div className='d-flex flex-column gap-2'>
                {currencies.map((currency) => (
                  <div
                    key={currency.companyCurrencyId}
                    className='d-flex justify-content-between align-items-center p-2 border rounded'
                  >
                    <div className='d-flex flex-column'>
                      <span className='fw-semibold caption'>
                        {currency.displayName}
                      </span>
                      <span className='mini font-light'>
                        {currency.code} · {currency.symbol}
                        {currency.locale ? ` · ${currency.locale}` : ''}
                      </span>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <span
                        className={`badge ${currency.isEnabled ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {currency.isEnabled ? t('company_currencies_active') : t('company_currencies_inactive')}
                      </span>
                      {Button && (
                        currency.isEnabled ? (
                          <>
                            <Button
                              variant='ghost'
                              iconStart='pencil'
                              size='sm'
                              ariaLabel={t('company_currencies_editAria')}
                              onClick={() => openEdit(currency)}
                            />
                            <Button
                              variant='ghost'
                              iconStart='trash'
                              size='sm'
                              ariaLabel={t('company_currencies_deleteAria')}
                              disabled={loadingRemove}
                              onClick={() => openDelete(currency)}
                            />
                          </>
                        ) : (
                          <Button
                            variant='ghost'
                            iconStart='arrowCounterclockwise'
                            size='sm'
                            ariaLabel={t('company_currencies_reactivateAria')}
                            disabled={loadingUpdate}
                            onClick={() => handleReactivate(currency)}
                          />
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'form' && (
          <div className='row g-3'>
            <div className='col-12 col-md-6'>
              {Input && (
                <Input
                  label={t('company_currencies_nameLabel')}
                  required
                  placeholder={t('company_currencies_namePlaceholder')}
                  value={form.name}
                  onChange={onChange('name')}
                />
              )}
            </div>
            <div className='col-12 col-md-6'>
              {Input && (
                <Input
                  label={t('company_currencies_symbolLabel')}
                  placeholder={t('company_currencies_symbolPlaceholder')}
                  value={form.symbol}
                  onChange={onChange('symbol')}
                  disabled={isEdit}
                />
              )}
            </div>
            <div className='col-12 col-md-6'>
              {Input && (
                <Input
                  label={t('company_currencies_codeLabel')}
                  placeholder={t('company_currencies_codePlaceholder')}
                  value={form.code}
                  onChange={onChange('code')}
                  disabled={isEdit}
                />
              )}
            </div>
            <div className='col-12 col-md-6'>
              {Input && (
                <Input
                  label={t('company_currencies_localeLabel')}
                  placeholder={t('company_currencies_localePlaceholder')}
                  value={form.locale}
                  onChange={onChange('locale')}
                  disabled={isEdit}
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        Modal={Modal}
        show={showConfirmDelete}
        title={t('company_currencies_disableTitle')}
        message={t('company_currencies_disableConfirm', { name: currencyToDelete?.displayName })}
        confirmLabel={t('company_currencies_disableBtn')}
        cancelLabel={t('company_currencies_cancel')}
        confirmVariant='danger'
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
          setCurrencyToDelete(null);
        }}
      />
    </>
  );
}
